import React, { useEffect, useState } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { Box, Menu, Tab, Tabs, TextField } from "@mui/material";
import { FaEdit, FaTrashAlt, FaRegCopy } from "react-icons/fa";
import { FaRegCirclePlay } from "react-icons/fa6";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { selectBuilding, selectDevice, selectRoom, selectSensor, selectUserData } from "../../features/store";
import DeleteConfig from "./DeleteConfig";
import UpdateConfig from "./UpdateConfig";
import InspectConfig from "./InspectConfig";
import { RectBtn } from "../../components/General";
import { useSnackbar } from "notistack";
import { updateBuilding } from "../../features/slices/buildingSlice";
import DeleteBuilding from "./DeleteBuilding";
import { PERMISSION } from "../../configs/global";
import DeleteNano from "./DeleteNano";
import { updateBuilding as updateBuildingApi } from "../../apis/Building.Api";

const ConfigTable = ({ currBuilding, setOpenAddConfig, setDuplicateConfig }) => {
    // ================= States ===================
    // Global
    const buildings = useSelector(selectBuilding);
    const rooms = useSelector(selectRoom);
    const sensors = useSelector(selectSensor);
    const devices = useSelector(selectDevice);
    const userData = useSelector(selectUserData);
    const dispatch = useDispatch();

    const [tabId, setTabId] = useState("room");
    const [displayRooms, setDisplayRooms] = useState([]);
    const [displayBuildings, setDisplayBuildings] = useState([]);
    const [displayNano, setDisplayNano] = useState([]);
    const [selectedBld, setSelectedBld] = useState({});
    const [selectedNano, setSelectedNano] = useState({});
    const [selectedConfig, setSelectedConfig] = useState({});

    // pop-up
    const [openInspectConfig, setOpenInspectConfig] = useState(false);
    const [openDeleteConfig, setOpenDeleteConfig] = useState(false);
    const [openUpdateConfig, setOpenUpdateConfig] = useState(false);
    const [openDeleteBuilding, setOpenDeleteBuilding] = useState(false);
    const [openDeleteNano, setOpenDeleteNano] = useState(false);
    // menu
    const [anchorUpdateBld, setAnchorUpdateBld] = useState(null);
    const openUpdateBld = Boolean(anchorUpdateBld);
    const [updatedBldName, setUpdatedBldName] = useState("");
    const [updateBldError, setUpdateBldError] = useState("");

    // snack
    const { enqueueSnackbar } = useSnackbar();
    // ================= Use Effect ===================
    useEffect(() => {
        getDisplayRooms();
    }, [currBuilding, rooms]);
    useEffect(() => {
        getDisplayBuildings();
    }, [currBuilding, buildings]);
    useEffect(() => {
        getDisplayNano();
    }, [currBuilding, devices, displayRooms]);
    // ================= Helper function ===================
    function getDisplayRooms() {
        let sortedRoomId = Object.keys(rooms).sort((a, b) => {
            return a.localeCompare(b, undefined, { sensitivity: "base" });
        });
        if (currBuilding?.buildingName === "All") {
            let temp = [];

            for (let rid of sortedRoomId) {
                // parse response to row object
                let room = rooms[rid];
                let obj = {
                    id: rid,
                    building: buildings[room?.buildingId]?.buildingName,
                    floor: room.floor,
                    room: room.roomName,
                    nanoId: room.nanoId,
                };
                temp.push(obj);
            }
            setDisplayRooms(temp);
        } else {
            let temp = [];
            for (let rid of sortedRoomId) {
                let room = rooms[rid];
                if (room?.buildingId === currBuilding?.buildingId) {
                    let obj = {
                        id: rid,
                        building: buildings[room?.buildingId].buildingName,
                        floor: room.floor,
                        room: room.roomName,
                        nanoId: room.nanoId,
                    };
                    temp.push(obj);
                }
            }
            setDisplayRooms(temp);
        }
    }
    function getDisplayBuildings() {
        let sortedBid = Object.keys(buildings).sort((a, b) => {
            return a.localeCompare(b, undefined, { sensitivity: "base" });
        });
        if (currBuilding?.buildingName === "All") {
            let temp = [];
            for (let bid of sortedBid) {
                let obj = { ...buildings[bid], id: bid };
                temp.push(obj);
            }
            setDisplayBuildings(temp);
        } else {
            let building = {
                ...buildings[currBuilding?.buildingId],
                id: currBuilding?.buildingId,
            };
            setDisplayBuildings([building]);
        }
    }
    function getDisplayNano() {
        let sortedNano = Object.keys(devices)
            .filter((did) => did.toUpperCase().startsWith("N"))
            .sort((a, b) => {
                return a.localeCompare(b, undefined, { sensitivity: "base" });
            });
        let temp = [];
        if (currBuilding?.buildingName === "All") {
            for (let nid of sortedNano) {
                let obj = { ...devices[nid], id: nid };
                temp.push(obj);
            }
        } else {
            let nids = new Set();
            for (let room of displayRooms) {
                if (room.nanoId) {
                    nids.add(room.nanoId);
                }
            }
            for (let nid of [...nids]) {
                let obj = { ...devices[nid], id: nid };
                temp.push(obj);
            }
        }
        setDisplayNano(temp);
    }
    async function confirmUpdateBld() {
        setUpdateBldError("");
        if (updatedBldName === "") {
            setUpdateBldError("Name cannot be empty");
            return;
        }
        let r = await updateBuildingApi({
            building_id: selectedBld?.buildingId,
            building_name: updatedBldName,
        });
        if (r.data.networkError) {
            enqueueSnackbar(r.data.message, { variant: "error" });
            return;
        }
        if (!r.data.indicator) {
            enqueueSnackbar(r.data.message + " This is possibly due to duplicated building name.", {
                variant: "error",
            });
            return;
        }
        enqueueSnackbar(r.data.message, { variant: "success" });
        dispatch(
            updateBuilding({
                buildingId: selectedBld?.buildingId,
                buildingName: updatedBldName,
            })
        );
        setUpdatedBldName("");
        setAnchorUpdateBld(null);
    }

    // ================= Columns ===================
    const columns = [
        {
            field: "room",
            headerName: "Room",
            flex: 2,
            minWidth: 100,
        },
        { field: "building", headerName: "Building", flex: 2, minWidth: 100 },
        { field: "floor", headerName: "Floor", flex: 2, minWidth: 80 },
        { field: "nanoId", headerName: "Nano ID", flex: 2, minWidth: 80 },
        {
            field: "actions",
            type: "actions",
            flex: 1,
            minWidth: 10,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<FaRegCirclePlay />}
                    label="Actions"
                    showInMenu
                    onClick={() => {
                        setOpenInspectConfig(true);
                        setSelectedConfig(params.row);
                    }}
                />,
                <GridActionsCellItem
                    icon={<FaEdit />}
                    label="Edit"
                    showInMenu
                    onClick={() => {
                        setOpenUpdateConfig(true);

                        setSelectedConfig(params.row);
                    }}
                />,
                <GridActionsCellItem
                    icon={<FaRegCopy />}
                    label="Duplicate"
                    showInMenu
                    onClick={() => {
                        setOpenAddConfig(true);

                        let buildingId;
                        for (let bid in buildings) {
                            if (buildings[bid]?.buildingName === params.row.building) {
                                buildingId = bid;
                                break;
                            }
                        }
                        let sensorList = [];
                        let uid = 0;
                        for (let sid in sensors) {
                            if (sensors[sid].roomId === params.row.id) {
                                sensorList.push({
                                    uid: uid,
                                    sensorId: sensors[sid].sensorId,
                                    sensorName: sensors[sid].sensorName,
                                    type: sensors[sid].sensorType,
                                });
                            }
                            uid++;
                        }
                        let deviceDict = { f: "", p: "" };
                        for (let did in devices) {
                            if (devices[did].roomId === params.row.id) {
                                if (did.toUpperCase().startsWith("F")) {
                                    deviceDict["f"] = did;
                                } else if (did.toUpperCase().startsWith("P")) {
                                    deviceDict["p"] = did;
                                }
                            }
                        }
                        let duplicate = {
                            buildingId: buildingId,
                            buildingName: params.row.building,
                            roomId: params.row.id,
                            roomName: params.row.room,
                            floor: params.row.floor,
                            sensorList: sensorList,
                            nanoId: params.row.nanoId || "",
                            deviceDict: deviceDict,
                        };
                        setDuplicateConfig(duplicate);
                    }}
                />,
                <GridActionsCellItem
                    icon={<FaTrashAlt color="#e5383b" />}
                    label="Delete"
                    showInMenu
                    sx={{ color: "#e5383b" }}
                    onClick={() => {
                        setOpenDeleteConfig(true);
                        setSelectedConfig(params.row);
                    }}
                />,
            ],
        },
    ];
    const buildingColumns = [
        {
            field: "buildingId",
            headerName: "Building ID",
            flex: 2,
            minWidth: 100,
        },
        {
            field: "buildingName",
            headerName: "Building",
            flex: 2,
            minWidth: 100,
        },

        {
            field: "actions",
            type: "actions",
            flex: 1,
            minWidth: 10,
            getActions: (params) =>
                [
                    <GridActionsCellItem
                        icon={<FaEdit />}
                        label="Edit"
                        onClick={(event) => {
                            setSelectedBld(params.row);
                            setUpdateBldError("");
                            setUpdatedBldName(params.row.buildingName);
                            setAnchorUpdateBld(event.currentTarget);
                        }}
                    />,

                    <GridActionsCellItem
                        icon={<FaTrashAlt color="#e5383b" />}
                        label="Delete"
                        sx={{ color: "#e5383b" }}
                        onClick={(event) => {
                            setSelectedBld(params.row);
                            setOpenDeleteBuilding(true);
                        }}
                    />,
                ].slice(
                    0,
                    userData.permission === PERMISSION.SUPER || userData.permission === PERMISSION.ADMIN ? 2 : 1
                ),
        },
    ];
    const nanoColumns = [
        {
            field: "deviceId",
            headerName: "Nano ID",
            flex: 2,
            minWidth: 100,
        },

        {
            field: "actions",
            type: "actions",
            flex: 1,
            minWidth: 10,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<FaTrashAlt color="#e5383b" />}
                    label="Delete"
                    sx={{ color: "#e5383b" }}
                    onClick={() => {
                        setOpenDeleteNano(true);
                        setSelectedNano(params.row);
                    }}
                />,
            ],
        },
    ];

    return (
        <div className="config-table">
            <Tabs
                value={tabId}
                onChange={(event, newValue) => {
                    setTabId(newValue);
                }}
                textColor="secondary"
                indicatorColor="secondary"
                TabIndicatorProps={{ style: { height: 3 } }}
            >
                <Tab label={<div className="tab-label">Building</div>} value={"building"} />
                <Tab label={<div className="tab-label">Nano</div>} value={"nano"} />
                <Tab label={<div className="tab-label">Room</div>} value={"room"} />
            </Tabs>
            <div className="header-division-line" />

            <Box sx={{ height: "65vh", width: "100%" }}>
                <DataGrid
                    rows={
                        (tabId === "room" && displayRooms) ||
                        (tabId === "building" && displayBuildings) ||
                        (tabId === "nano" && displayNano)
                    }
                    columns={
                        (tabId === "room" && columns) ||
                        (tabId === "building" && buildingColumns) ||
                        (tabId === "nano" && nanoColumns)
                    }
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 10 },
                        },
                    }}
                    pageSizeOptions={[10, 20]}
                    sx={{
                        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                            outline: "none",
                        },
                        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                            outline: "none",
                        },
                        border: 0,
                    }}
                    disableRowSelectionOnClick
                />
            </Box>

            <div className="pop-ups">
                <DeleteBuilding
                    openDelete={openDeleteBuilding}
                    setOpenDelete={setOpenDeleteBuilding}
                    selectedConfig={selectedBld}
                    setSelectedConfig={setSelectedBld}
                />
                <DeleteNano
                    openDelete={openDeleteNano}
                    setOpenDelete={setOpenDeleteNano}
                    selectedConfig={selectedNano}
                    setSelectedConfig={setSelectedNano}
                />
                <DeleteConfig
                    openDelete={openDeleteConfig}
                    setOpenDelete={setOpenDeleteConfig}
                    selectedConfig={selectedConfig}
                    setSelectedConfig={setSelectedConfig}
                />
                <UpdateConfig
                    openUpdate={openUpdateConfig}
                    setOpenUpdate={setOpenUpdateConfig}
                    selectedConfig={selectedConfig}
                    setSelectedConfig={setSelectedConfig}
                />
                <InspectConfig
                    openInspect={openInspectConfig}
                    setOpenInspect={setOpenInspectConfig}
                    selectedConfig={selectedConfig}
                    setSelectedConfig={setSelectedConfig}
                />
            </div>

            <div className="menus">
                <Menu
                    id="update-bld-menu"
                    anchorEl={anchorUpdateBld}
                    open={openUpdateBld}
                    onClose={() => {
                        setAnchorUpdateBld(null);
                    }}
                    anchorOrigin={{ vertical: "center", horizontal: "left" }}
                    transformOrigin={{ vertical: "center", horizontal: "right" }}
                >
                    <div className="update-menu-content">
                        <div className="fields">
                            <TextField
                                onKeyDown={(e) => e.stopPropagation()}
                                value={updatedBldName}
                                onChange={(e) => {
                                    setUpdatedBldName(e.target.value);
                                }}
                                inputProps={{ maxLength: 30 }}
                                variant="standard"
                                label={"Building Name"}
                                error={updateBldError !== ""}
                                helperText={updateBldError}
                            />
                        </div>
                        <div className="update-menu-btn-wrapper">
                            <RectBtn onClick={confirmUpdateBld}>
                                <p>Confirm</p>
                            </RectBtn>
                        </div>
                    </div>
                </Menu>
            </div>
        </div>
    );
};

export default ConfigTable;
